import { NgClass } from '@angular/common';
import { Component, DoCheck, EventEmitter, Input, IterableDiffer, IterableDiffers, OnDestroy, Optional, Output } from '@angular/core';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { DateInputComponent } from 'components/input/date-input/date-input.component';
import { LoadingDirective } from 'components/shared/skeleton/loading.directive';
import { InputType } from 'enums/InputType';
import { LedgerPostType } from 'enums/LedgerPostType';
import { MessageType } from 'enums/MessageType';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { LedgerPostModel } from 'models/generated/Entities.Accounting.LedgerPost';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { Layout } from 'modules/layout';
import { ListView } from 'modules/list-view';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { CompanyDataProvider, CompanyDataProviderFactory } from 'services/company-data-provider';
import { DateService } from 'services/date.service';
import { FormValidationService } from 'services/form-validation.service';
import { NavigationService } from 'services/navigation.service';
import { PushService } from 'services/push.service';
import { CompanyInvoicesComponent } from 'components/company/invoice/company-invoices.component';

@Component({
	selector: 'ledger-posts',
	templateUrl: './ledger-posts.component.html',
	imports: [Layout, LoadingDirective, NgClass, ListView, DateInputComponent, CustomInputComponent, CurrencyPipe, TranslatePipe, CustomDatePipe, ProductNamePipe]
})
export class LedgerPostsComponent implements DoCheck, OnDestroy {

	private companyDataProvider?: CompanyDataProvider;

	@Input() public historicalDate?: Date;
	@Input() public editMode = false;
	@Input() public editMigratedData = false;
	@Input() public displayProductName = true;
	@Input() public splitByKey?: string;
	@Input() public allowChangingTotalValue = false;
	@Input() public set showClosedPosts(value: boolean) { this._showClosedPosts = value; this.filterLedgerPosts(); }
	public get showClosedPosts() { return this._showClosedPosts; }
	@Input() public overridePosts?: LedgerPostModel[];
	@Output() public postsChanged = new EventEmitter<void>();

	public allPosts: LedgerPostModel[] = [];
	public posts: LedgerPostModel[] = [];
	public disabledPosts: LedgerPostModel[] = [];
	public ledgerPostType = LedgerPostType;
	public loading = true;
	public inputType = InputType;
	public today: Date;
	public productPlanMap = new Map<string, ProductPlanModel>();

	public totalAccountingPlanValue = 0;

	private _totalLedgerValue = 0;
	public set totalLedgerValue(value: number) { this._totalLedgerValue = value; this.totalLedgerValueChanged.next(value); }
	public get totalLedgerValue() { return this._totalLedgerValue; }
	@Output() public totalLedgerValueChanged = new EventEmitter<number>();

	public postedInvoiceValue = 0;
	public unpostedInvoiceValue = 0;
	public creditedValue = 0;
	public closedInvoiceValue = 0;
	public closedValue = 0;

	private _showClosedPosts = true;
	private billingPlanSubscription: Subscription;
	private listOfPostsIsPristine = true;
	private postsDiffer: IterableDiffer<LedgerPostModel>;
	private disabledDiffer: IterableDiffer<LedgerPostModel>;

	private _accountingPlan?: AccountingPlanModel;
	@Input() set accountingPlan(accountingPlan: AccountingPlanModel | undefined) {
		this._accountingPlan = accountingPlan;
		this.filterLedgerPosts();
	}
	get accountingPlan() {
		return this._accountingPlan;
	}

	private _productPlans: ProductPlanModel[] = [];
	@Input() set productPlans(value: ProductPlanModel[]) {
		if (this._productPlans?.length == 0 && value?.length == 0) return;
		this._productPlans = value;
		this.reloadLedgerPosts();
	}
	get productPlans() { return this._productPlans; }

	async reloadLedgerPosts(silent = false): Promise<void> {

		this.productPlanMap = new Map(this.productPlans.map(plan => [plan.id!, plan]));
		const postsPromise = this.companyDataProvider
			? this.companyDataProvider.getLedgerPostsByProductPlanIds(Array.from(this.productPlanMap.keys()), this.historicalDate)
			: this.accountingService.getLedgerPostsByProductPlanIds(Array.from(this.productPlanMap.keys()), this.historicalDate);
		if (silent) await postsPromise; // If reloading silently (due to push messages), don't show loaders, just await the promise and load in new data when it's ready

		this.loading = true;
		this.posts = [];
		this.allPosts = [];

		const posts = (await postsPromise).map(p => Object.assign(new LedgerPostModel(), structuredClone(p))); // We always reload posts from dataProvider or service when changes are pushed, so working with a structured clone here allows us to cancel out of edits

		posts.forEach(post => {
			post.year = post.plannedDate?.getFullYear();
		});

		if (this.overridePosts) this.allPosts = this.overridePosts;
		else this.allPosts = posts;
		this.listOfPostsIsPristine = true;
		this.filterLedgerPosts();
		this.loading = false;
	}
	
	clearEditablePosts() {
		this.allPosts = this.posts.filter(post => !this.isEditable(post, true));
	}

	removePost(post: LedgerPostModel) {
		this.allPosts.splice(this.allPosts.indexOf(post), 1);
		this.listOfPostsIsPristine = false;
		this.filterLedgerPosts();
	}

	copyDown(post: LedgerPostModel, ledgerPostOrder: LedgerPostModel[]) {
		for (let i = ledgerPostOrder.indexOf(post); i < ledgerPostOrder.length; i++) {
			if (this.isEditable(ledgerPostOrder[i])) ledgerPostOrder[i].value = post.value;
		}
		this.calculateLedgerValues();
	}

	addPost(data?: Object) {
		const newPost = new LedgerPostModel();
		newPost.type = LedgerPostType.Invoice;
		newPost.accountingPlan = this.accountingPlan;
		newPost.accountingPlanId = this.accountingPlan?.id;
		newPost.plannedDate = this.accountingPlan?.endDate;
		newPost.value = 0;
		if (data) Object.assign(newPost, data);
		this.allPosts.push(newPost);
		this.listOfPostsIsPristine = false;
		this.filterLedgerPosts();
	}

	private filterLedgerPosts() {
		this.allPosts.sort((a, b) => {
			if (a.plannedDate && b.plannedDate) return b.plannedDate.getTime() - a.plannedDate.getTime();
			if (b.plannedDate) return -1;
			if (a.plannedDate) return 1;
			return 0;
		});

		this.posts = this.allPosts
			.filter(p => !this.accountingPlan || p.accountingPlanId === this.accountingPlan!.id) // filter by selected accountingplan if selected
			.filter(p => this.accountingPlan || (p.accountingPlan!.startDate && p.accountingPlan!.startDate < new Date(Date.now()))) // filter out upcoming plans if no accountingplan is selected

		this.calculateLedgerValues();

		if (!this.showClosedPosts)
			this.posts = this.posts.filter(p => p.type != LedgerPostType.MigratedInvoice && p.type != LedgerPostType.EqualizingCounterPost && p.type != LedgerPostType.CounterPost && (p.ledgerHeaderId == null || !p.ledgerHeader!.closed));

		// Force update differs, so they don't report a change and run filter more than necessary
		this.postsDiffer.diff(this.allPosts);
		this.disabledDiffer.diff(this.disabledPosts);

		this.postsChanged.emit();
	}

	calculateLedgerValues() {

		// Add up all values by smallest accuracy (*100) to avoid floating point errors
		let totalAccountingPlanValue = 0;
		let totalLedgerValue = 0;
		let postedInvoiceValue = 0;
		let unpostedInvoiceValue = 0;
		let closedValue = 0;
		let closedInvoiceValue = 0;
		let creditedValue = 0;

		const uniqueAccountingPlans: AccountingPlanModel[] = [];

		this.posts.forEach(post => {

			if (this.isDisabled(post)) return;

			const value = Math.round(this.getValue(post) * 100);
			const isInvoice = (post.type == LedgerPostType.Invoice || post.type == LedgerPostType.StartupInvoice || post.type == LedgerPostType.MigratedInvoice || post.type == LedgerPostType.EqualizingCounterPost);
			const isCreditMemo = (post.type == LedgerPostType.CreditMemo || post.type == LedgerPostType.CounterPost);
			const isClosed = (post.ledgerHeader?.closed || post.type == LedgerPostType.MigratedInvoice || post.type == LedgerPostType.CounterPost || post.type == LedgerPostType.EqualizingCounterPost);
			const isPosted = (post.ledgerHeader?.postedDate || post.type == LedgerPostType.MigratedInvoice || post.type == LedgerPostType.CounterPost || post.type == LedgerPostType.EqualizingCounterPost);

			totalLedgerValue += value;
			
			if (isClosed) {
				closedValue += value;
			}
			if (isInvoice) {
				if (isPosted) postedInvoiceValue += value;
				else unpostedInvoiceValue += value;

				if (isClosed) closedInvoiceValue += value;
			}
			if (isCreditMemo) {
				creditedValue += value;
			}

			if (!uniqueAccountingPlans.find(plan => plan.id === post.accountingPlan!.id)) 
				uniqueAccountingPlans.push(post.accountingPlan!);
		});

		uniqueAccountingPlans.forEach(plan => {
			totalAccountingPlanValue += plan.totalPlanValue * 100;
		});

		this.totalAccountingPlanValue = totalAccountingPlanValue / 100; // *should* always be exactly the same value as totalLedgerValue, but included to make potential errors visible
		this.totalLedgerValue = totalLedgerValue / 100;
		this.postedInvoiceValue = postedInvoiceValue / 100;
		this.unpostedInvoiceValue = unpostedInvoiceValue / 100;
		this.closedValue = closedValue / 100;
		this.closedInvoiceValue = closedInvoiceValue / 100;
		this.creditedValue = creditedValue / 100;

		this.refreshValidState();
	}

	private refreshValidState() {
		this.formValidation.fieldStateChanged(this, this.allowChangingTotalValue || this.totalLedgerValue === this.totalAccountingPlanValue, this.listOfPostsIsPristine);
	}

	getValue(post: LedgerPostModel) {
		return (post.type === LedgerPostType.CreditMemo || post.type === LedgerPostType.CounterPost) ? -post.value : post.value;
	}

	markAsPristine() {
		this.listOfPostsIsPristine = true;
		this.refreshValidState();
	}

	isOverdue(post: LedgerPostModel): boolean {
		let dueDate = post.ledgerHeader?.dueDate;
		if (!dueDate) return false;

		dueDate = new Date(dueDate.getTime());
		dueDate.setDate(dueDate.getDate() + 1);
		dueDate.setHours(0, 0, 0, 0);

		return !post.ledgerHeader?.closed && dueDate < new Date();
	}

	isClosed(post: LedgerPostModel): boolean {
		return (post.type == LedgerPostType.MigratedInvoice) || (post.type == LedgerPostType.CounterPost) || (post.type == LedgerPostType.EqualizingCounterPost) || (post.ledgerHeader?.closed == true);
	}

	isOpen(post: LedgerPostModel): boolean {
		return post.ledgerHeader?.postedDate != undefined && post.ledgerHeader.closed == false;
	}

	isEditable(post: LedgerPostModel, ignoreEditMode?: boolean): boolean {

		// In "edit migrated data" mode, only migrated invoices should be editable
		if (this.editMigratedData) return post.type == LedgerPostType.MigratedInvoice;

		return (this.editMode || ignoreEditMode == true)
			&& (post.type == LedgerPostType.Invoice || post.type == LedgerPostType.StartupInvoice || post.type == LedgerPostType.CreditMemo)
			&& !post.ledgerHeader
	}

	isDisabled(post: LedgerPostModel): boolean {
		return this.disabledPosts.indexOf(post) >= 0;
	}

	getEditedLedgerPosts(ignoreEditMode?: boolean) {
		if (!ignoreEditMode && !this.editMode && !this.editMigratedData) throw new Error('Ledger Posts not in edit mode');
		return this.posts.filter(p => this.isEditable(p, ignoreEditMode) && !this.isDisabled(p));
	}

	reloadIfMatchingUpdateInfo(data: { accountingPlanId: string, productPlanId: string }) {
		if (this.editMode || this.editMigratedData) return; // Don't reload if we're editing. Parent component will reload if edit mode can be canceled.

		if (this.accountingPlan?.id === data.accountingPlanId) this.reloadLedgerPosts(true);
		else if (this.productPlans.some(p => p.id === data.productPlanId)) this.reloadLedgerPosts(true);
	}

	navigateToInvoice(post: LedgerPostModel) {
		const companyId = post.accountingPlan?.companyId;
		const documentNo = post.ledgerHeader?.postedDocumentNo;
		const brandId = post.ledgerHeader?.brandId;
		if (!companyId || !documentNo || !brandId) return;

		this.navigation.navigateByUrl(`/company-invoices/${companyId}/${brandId}/${documentNo}`);
	}

	constructor(
		private readonly accountingService: AccountingService,
		private readonly navigation: NavigationService,
		private readonly formValidation: FormValidationService,
		private readonly pushService: PushService,
		dateService: DateService,
		iterableDiffers: IterableDiffers,
		@Optional() companyDataProviderFactory?: CompanyDataProviderFactory) {

		if (companyDataProviderFactory) {
			this.companyDataProvider = companyDataProviderFactory.getProviderForCurrentScope();
			this.billingPlanSubscription = this.companyDataProvider.onBillingPlanChanges(data => this.reloadIfMatchingUpdateInfo(data));
		}
		else {
			this.billingPlanSubscription = this.pushService.onChanges<{ accountingPlanId: string, productPlanId: string }>(MessageType.BillingPlanChanges).subscribe(message => this.reloadIfMatchingUpdateInfo(message.data));
		}

		this.today = dateService.today();
		this.postsDiffer = iterableDiffers.find(this.allPosts).create<LedgerPostModel>();
		this.disabledDiffer = iterableDiffers.find(this.disabledPosts).create<LedgerPostModel>();
	}
	ngOnDestroy(): void {
		this.billingPlanSubscription.unsubscribe();
	}

	ngDoCheck() {
		if (this.postsDiffer.diff(this.allPosts) || this.disabledDiffer.diff(this.disabledPosts)) this.filterLedgerPosts();
	}
}
